/**
 * Single-question assessment step: progress, stem, options, footer (Figma steps 02–05).
 * Auto-advances after 350ms on selection (except the final question).
 * @agentic members
 */

import { useRef, useEffect, useId } from "react";
import {
  Paper,
  Stack,
  Title,
  Text,
  Button,
  Progress,
  Group,
  Box,
  Alert,
} from "@mantine/core";

import type { QuestionnaireItem } from "@medplum/fhirtypes";

import styles from "./LinearAssessmentView.module.css";

type LinearAssessmentViewProps = {
  sectionLabel: string;
  questionOrdinal: number;
  totalQuestions: number;
  instructions: string | undefined;
  item: QuestionnaireItem;
  value: string | undefined;
  advocateFooterName: string;
  onAnswer: (questionnaireUrl: string, linkId: string, value: string) => void;
  onBack: () => void;
  onNext: () => void;
  questionnaireUrl: string;
  canGoBack: boolean;
  isLastQuestion: boolean;
  showCrisisAlert?: boolean;
  crisisAlertThresholds?: string[];
  /** Per-question skip (§8.4): always available, advances without an answer. */
  onSkip?: () => void;
  /** One-action section decline (§8.5–§8.6): settles the rest of the section. */
  onDeclineSection?: () => void;
  /** The section name the decline action is scoped to (accessible name). */
  declineSectionLabel?: string;
};

/**
 * Renders one question with global progress, custom radios, and advocate footer.
 * @agentic members
 */
export const LinearAssessmentView = ({
  sectionLabel,
  questionOrdinal,
  totalQuestions,
  instructions,
  item,
  value,
  advocateFooterName,
  onAnswer,
  onBack,
  onNext,
  questionnaireUrl,
  canGoBack,
  isLastQuestion,
  showCrisisAlert = false,
  crisisAlertThresholds,
  onSkip,
  onDeclineSection,
  declineSectionLabel,
}: LinearAssessmentViewProps) => {
  const linkId = item.linkId ?? "";
  const options = item.answerOption ?? [];
  const answered = value !== undefined && value !== "";
  const progress =
    totalQuestions > 0 ? (questionOrdinal / totalQuestions) * 100 : 0;
  const autoAdvanceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const questionTitleId = useId();

  useEffect(() => {
    return () => {
      if (autoAdvanceRef.current) {
        clearTimeout(autoAdvanceRef.current);
      }
    };
  }, []);

  // On each step (the component remounts per question) move focus to the
  // question heading so keyboard and screen-reader users are oriented to the
  // new question instead of being left on a stale control (WCAG 2.4.3).
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const handleSelect = (optValue: string) => {
    if (autoAdvanceRef.current) {
      clearTimeout(autoAdvanceRef.current);
    }
    onAnswer(questionnaireUrl, linkId, optValue);
    const triggersCrisis = crisisAlertThresholds?.includes(optValue) ?? false;
    if (!isLastQuestion && !triggersCrisis) {
      autoAdvanceRef.current = setTimeout(() => {
        onNext();
      }, 350);
    }
  };

  return (
    <Paper radius="lg" p="md" shadow="sm" className={styles.card}>
      <Stack gap="md">
        <Group justify="space-between" align="center" gap="xs">
          <Text
            size="xs"
            tt="uppercase"
            fw={400}
            style={{ letterSpacing: "0.04em", opacity: 0.7, color: "#2a1a2e" }}
          >
            {sectionLabel}
          </Text>
          <Text size="xs" style={{ opacity: 0.7, color: "#2a1a2e" }}>
            {questionOrdinal} / {totalQuestions}
          </Text>
        </Group>
        <Progress
          value={progress}
          size={4}
          radius="xl"
          color="plum.5"
          aria-label={`Question ${String(questionOrdinal)} of ${String(totalQuestions)}`}
        />
        {canGoBack && (
          <Button
            variant="subtle"
            size="compact-xs"
            onClick={onBack}
            style={{ opacity: 0.7, color: "#2a1a2e" }}
          >
            ← Back
          </Button>
        )}
        {instructions && (
          <Text
            size="sm"
            className={styles.stem}
            style={{ opacity: 0.7, color: "#2a1a2e" }}
          >
            {instructions}
          </Text>
        )}
        <Title
          order={4}
          id={questionTitleId}
          ref={headingRef}
          tabIndex={-1}
          className={styles.questionTitle}
          c="#391640"
        >
          {item.text}
        </Title>
        <div role="radiogroup" aria-labelledby={questionTitleId}>
          <Stack gap="xs">
            {options.map((opt, optIdx) => {
              const coding = opt.valueCoding;
              const optValue = coding?.code ?? String(optIdx);
              const optLabel =
                coding?.display ??
                opt.valueString ??
                `Option ${String(optIdx + 1)}`;
              const isSelected = value === optValue;
              return (
                <button
                  key={optValue}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  aria-label={optLabel}
                  className={`${styles.option} ${isSelected ? styles.optionSelected : ""}`}
                  onClick={() => handleSelect(optValue)}
                >
                  <span
                    className={`${styles.radioIndicator} ${isSelected ? styles.radioIndicatorSelected : ""}`}
                  >
                    {isSelected && <span className={styles.checkmark}>✓</span>}
                  </span>
                  <span className={styles.optionLabel}>{optLabel}</span>
                </button>
              );
            })}
          </Stack>
        </div>
        {showCrisisAlert && (
          <Alert
            color="red"
            radius="md"
            title="Please call 911 or visit the nearest emergency room."
            styles={{
              title: { fontSize: "var(--mantine-font-size-sm)" },
              root: { borderColor: "var(--mantine-color-red-4)" },
            }}
          >
            <Text size="sm">
              If you need immediate support, you can also contact the 988
              Suicide &amp; Crisis Lifeline by calling or texting{" "}
              <Text span fw={700} component="span">
                988
              </Text>
              .
            </Text>
          </Alert>
        )}
        {showCrisisAlert && answered && (
          <Button size="lg" fullWidth onClick={onNext}>
            Continue
          </Button>
        )}
        {onSkip && (
          <Button
            variant="subtle"
            size="compact-sm"
            onClick={() => {
              if (autoAdvanceRef.current) {
                clearTimeout(autoAdvanceRef.current);
              }
              onSkip();
            }}
            style={{ opacity: 0.75, color: "#2a1a2e" }}
          >
            Skip this question
          </Button>
        )}
        {onDeclineSection && (
          <Button
            variant="subtle"
            size="compact-xs"
            onClick={() => {
              // A just-given answer's pending auto-advance must not race the
              // decline: cancel it so a failed decline stays on this question.
              if (autoAdvanceRef.current) {
                clearTimeout(autoAdvanceRef.current);
              }
              onDeclineSection();
            }}
            aria-label={
              declineSectionLabel
                ? `Skip the rest of this section: ${declineSectionLabel}`
                : "Skip the rest of this section"
            }
            style={{ opacity: 0.6, color: "#2a1a2e" }}
          >
            Skip the rest of this section
          </Button>
        )}
        <Box className={styles.footer}>
          <Text
            size="xs"
            ta="center"
            style={{ opacity: 0.7, color: "#2a1a2e" }}
          >
            If this is an emergency, please call 911 or visit the nearest
            emergency room.
          </Text>
          <Text
            size="xs"
            ta="center"
            style={{ opacity: 0.7, color: "#2a1a2e" }}
          >
            {advocateFooterName} will review your responses
          </Text>
        </Box>
        {isLastQuestion && !showCrisisAlert && (
          <Button size="lg" fullWidth disabled={!answered} onClick={onNext}>
            Submit responses
          </Button>
        )}
      </Stack>
    </Paper>
  );
};
